'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { useToast } from '@/components/ui/use-toast';
import { ErrorDisplay } from '@/components/ui/error-display';
import { useCreateProduct, type CreateProductInput } from '@/lib/api/products';
import { ProductInformationForm, ProductSettingsForm, ProductVariantsForm } from '@/components/form/product';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';
import { createProductSchema, type CreateProductFormData } from './validation';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

const DEFAULT_VALUES = {
  name: '',
  description: '',
  unitLabel: '',
  accountingCode: '',
  taxCategory: '',
  active: true,
  isShippable: true,
  isTaxable: true,
  autoSync: true,
  variants: [],
};

const CreateProductPageInner = () => {
  const { t } = useTranslation(['products', 'common']);
  const { storeRoutes, navigate, id: storeId } = useStoreNavigate();
  const { toast } = useToast();

  const createProductMutation = useCreateProduct();
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);

  const methods = useForm<CreateProductFormData>({
    defaultValues: {
      name: '',
      description: null,
      unitLabel: null,
      accountingCode: null,
      taxCategory: null,
      mappedTaxCategoryId: null,
      active: true,
      isShippable: true,
      isTaxable: true,
      autoSync: true,
      variants: [],
    },
    resolver: zodResolver(createProductSchema),
    mode: 'onBlur',
  });
  const {
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const handleCreateProduct = async (data: CreateProductFormData) => {
    try {
      setFormError(null);

      const input: CreateProductInput = {
        ...data,
        storeId,
      };

      await createProductMutation.mutateAsync(input);

      toast({
        title: t('productCreated'),
        description: t('productCreatedSuccess'),
      });
      navigate(storeRoutes.PRODUCTS());
    } catch (error) {
      const parsedError = parseTRPCError(error);
      setFormError(parsedError);

      if (!parsedError.isValidationError) {
        toast({
          title: t('createError'),
          description: parsedError.message,
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleCreateProduct)}>
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-4">
                <BackButton to={storeRoutes.PRODUCTS()} />
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">
                    {t('createProduct')}
                  </h1>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('createProductDescription')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(storeRoutes.PRODUCTS())}
                  disabled={isSubmitting}
                >
                  {t('cancel')}
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting || createProductMutation.isPending}>
                  {isSubmitting || createProductMutation.isPending
                    ? t('creating')
                    : t('createProduct')}
                </Button>
              </div>
            </div>

            {/* Error Display */}
            {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

            {/* Main Content */}
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              {/* Main content - 2/3 width */}
              <div className="space-y-6 lg:col-span-2">
                <ProductInformationForm />
                <ProductVariantsForm storeId={storeId} />
              </div>

              {/* Sidebar - 1/3 width */}
              <div className="space-y-6">
                <ProductSettingsForm />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export const CreateProductPage = withRequiredPermission(CreateProductPageInner, PERMISSIONS.STORES_MANAGE);
export default CreateProductPage;
